/**
 * Deliverable SECTIONS — the packages INSIDE a package.
 *
 * A template deliverable can nest sections one level deep: a Paper Lunch Bag
 * holding the muffin in a Kraft Sandwich Bag and the oranges in two 8 oz cups.
 * The materializer writes that tree onto `shipment_lines` (root package line →
 * child package lines → food leaves, linked by `parentLineId`), and the wire
 * carries every line. The chef surfaces used to read only the root, so the row
 * said "Paper Lunch Bag → Muffin: 1 Muffin + Orange Wedges: 1/2 Cup" and the
 * packet "60x Paper Lunch Bags" — the kitchen could not see what goes in which
 * inner package, nor that the oranges ride in TWO cups.
 *
 * This reads the tree back: per deliverable, the food the root holds directly,
 * then one section per inner package (its SKU, how many ride in each meal, and
 * the food it holds). Identical inner packages merge, so two orange cups are
 * ONE section with `perMeal: 2`. Pure.
 */
import type { KitchenShipmentLineSummary } from '../../api/kitchen-orders'
import type { DeliverableSection } from './types'

type Line = Pick<
  KitchenShipmentLineSummary,
  | 'id'
  | 'parentLineId'
  | 'componentId'
  | 'plannedQuantity'
  | 'packagingSkuName'
  | 'isSupply'
  | 'packagingKind'
  | 'isBulk'
>

/** One part of a deliverable, before names and portions are attached. */
export interface DeliverableSectionPlan {
  /** The inner package's SKU; null for the food the outer package holds directly. */
  packaging: string | null
  /** How many of that inner package ride in each meal (two orange cups = 2). */
  perMeal: number
  /** The foods inside, in first-seen order. */
  componentIds: string[]
}

function isFoodPackage(line: Line): boolean {
  return line.packagingSkuName !== null && line.isSupply !== true && line.packagingKind !== 'UTENSIL'
}

/**
 * The sections of ONE deliverable from its package lines and food leaves.
 * Null when it has none — a bulk deliverable (pans are packed by the bulk
 * plan, not by section), a deliverable whose food all sits in the outer
 * package, or one whose inner packages hold no food (a lid, a napkin): the row
 * then reads exactly as before.
 */
export function planDeliverableSections(
  packageLines: ReadonlyArray<Line>,
  leaves: ReadonlyArray<Line>,
): DeliverableSectionPlan[] | null {
  if (packageLines.some((line) => line.isBulk === true)) {
    return null
  }
  const roots = packageLines.filter((line) => line.parentLineId === null)
  const rootIds = new Set(roots.map((line) => line.id))
  const meals = roots.reduce((sum, line) => sum + line.plannedQuantity, 0)
  if (roots.length === 0 || meals <= 0) {
    return null
  }
  const foodsIn = (packageId: string): string[] => [
    ...new Set(leaves.filter((leaf) => leaf.parentLineId === packageId && leaf.componentId).map((l) => l.componentId!)),
  ]
  const sections = new Map<string, DeliverableSectionPlan & { quantity: number }>()
  for (const inner of packageLines) {
    if (inner.parentLineId === null || !rootIds.has(inner.parentLineId) || !isFoodPackage(inner)) {
      continue
    }
    const componentIds = foodsIn(inner.id)
    if (componentIds.length === 0) {
      continue
    }
    // Grade tiers repeat the same inner package under each tier's root; they
    // and the second orange cup all merge here, summing what ships.
    const key = `${inner.packagingSkuName}|${[...componentIds].sort().join(',')}`
    const section = sections.get(key) ?? { packaging: inner.packagingSkuName, perMeal: 0, componentIds, quantity: 0 }
    section.quantity += inner.plannedQuantity
    sections.set(key, section)
  }
  if (sections.size === 0) {
    return null
  }
  const direct = [...new Set(roots.flatMap((root) => foodsIn(root.id)))]
  const inner = [...sections.values()].map(({ packaging, componentIds, quantity }) => ({
    packaging,
    perMeal: Math.max(1, Math.round(quantity / meals)),
    componentIds,
  }))
  return direct.length > 0 ? [{ packaging: null, perMeal: 1, componentIds: direct }, ...inner] : inner
}

/** The foods of one section as words: "Orange Wedges, 1/2 Cup" or "Penne, 1 Cup + Meat Sauce, 2/3 Cup". */
export function sectionFoods(section: Pick<DeliverableSection, 'items'>): string {
  return section.items.map((item) => (item.serving ? `${item.name}, ${item.serving}` : item.name)).join(' + ')
}

/**
 * One line per section for the screen row, under the outer package:
 * "Housemade Muffin, 1 Muffin · Kraft Sandwich Bag", "Orange Wedges, 1/2 Cup ·
 * 2x Paper Food Cup with Lid (8 oz)". Food the outer package holds directly
 * has no package suffix.
 */
export function sectionRowLines(sections: ReadonlyArray<DeliverableSection>): string[] {
  return sections.map((section) => {
    const foods = sectionFoods(section)
    if (!section.packaging) {
      return foods
    }
    return `${foods} · ${section.perMeal > 1 ? `${section.perMeal}x ` : ''}${section.packaging}`
  })
}

/** A stable key for a plan, so rows merge only when their sections match. */
export function deliverableSectionsKey(plans: ReadonlyArray<DeliverableSectionPlan> | null): string {
  return plans ? plans.map((p) => `${p.packaging ?? ''}*${p.perMeal}:${p.componentIds.join('+')}`).join(';') : ''
}
